import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Input, FormInstance, theme, Row, Col, Skeleton, Divider } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';

import { EPSelect, EPSelectOption } from '@/components';
import { DeviceType } from '@/constants/deviceTypeEnum';
import ProductionFloorWidget from '@/comp/common/icons/ProductionFloorWidget';
import { useResponsive } from '@/hooks/useResponsive';

import { Entity } from '../../../NodeWidget.types';
import { getFieldSelectProps, useFormHelper } from '../../EditAssetDrawer.utils';
import { EditProductionLineFormContainer } from './EditProductionLineForm.styles';
import { mapFormValuesToPayload, useInitialValues } from './EditProductionLineForm.utils';
import ConnectionSelector from '../ConnectionSelector/ConnectionSelector';
import ConnectionList from '../ConnectionList/ConnectionList';

const EditProductionLineForm = observer(
  ({
    entity,
    form,
    afterOpenChange,
    messageApi,
  }: {
    entity: Entity;
    form: FormInstance;
    afterOpenChange: boolean;
    messageApi: MessageInstance;
  }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const { token } = theme.useToken();

    const { deviceModelOptions, isLoadingModels, onFinish, onModelSearch, initialValues, deviceConnectionOptions } =
      useFormHelper(
        t,
        entity,
        afterOpenChange,
        messageApi,
        mapFormValuesToPayload,
        useInitialValues,
        entity.deviceType,
      );

    const isLoading = !afterOpenChange || isLoadingModels;

    if (isLoading) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }

    return (
      <EditProductionLineFormContainer>
        <Form layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
          <span className="horizontal-label">{t('connect')}</span>
          <Row className="connected-from-row">
            <Col span={8}>
              <Form.Item label=" " name="connectFrom">
                <ConnectionSelector
                  label="from"
                  options={deviceConnectionOptions.filter(option => option.deviceType !== DeviceType.ProductionFloor)}
                  form={form}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <div className="pipe" />
            </Col>
            <Col span={8} className="widget">
              <ProductionFloorWidget width={120} height={140} color={token.colorBorder} iconColor={token.colorBgBase} />
            </Col>
          </Row>
          <ConnectionList form={form} field="from" />
          <ConnectionList form={form} field="to" />
          <Divider className="divider" />
          <Form.Item label={t('name')} name="name">
            <Input size="large" />
          </Form.Item>
          <Form.Item label={t('modelSpec')} name="deviceModelId" required rules={[{ required: true, message: t('required') }]}>
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectModelSpec'), 'deviceModelId')}
              loading={isLoadingModels}
              onSearch={onModelSearch}
              filterOption={false}
            >
              {deviceModelOptions.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
        </Form>
      </EditProductionLineFormContainer>
    );
  },
);

export default EditProductionLineForm;
